
  import { Component, Watch } from 'vue-property-decorator'
  import { plainToInstance } from 'class-transformer'
  import BaseForm from '@/components/forms/view/BaseForm.vue'
  import { Form } from '@/entities/public/Resource/metadata'
  import { fixAmountFormatted, isValidNumber, stringifySafe } from '@/utils/general'

  import LinkedPerson from '@/components/forms/fields/LinkedPerson.vue'
  import GFiles from '@/components/core/files/GFiles.vue'
  import GRadioButton from '@/components/core/input/GRadioButton.vue'

  import { GForm } from '@/components/forms/GForm'
  import { SocialNetworks, Stock } from '@/entities/sales'
  import GCostField from '@/components/core/input/GCostField.vue'
  import GAlert from '@/components/core/alert/GAlert.vue'

@Component({
  components: { GAlert, GCostField, GRadioButton, GFiles, LinkedPerson, BaseForm },
  methods: { fixAmountFormatted },
  computed: {},
})
  export default class AdvertiserPublishStockForm extends GForm {
  stock: Stock = plainToInstance(Stock, {})
  message = ''
  ready = false
  idProcess = null
  isUploadingFile = false
  fileParametersVideo = []
  componentKeysVideo = []
  fileParametersPhoto = []
  componentKeysPhoto = []
  processInspectionId = null
  showDetail = false
  categoryComponentMaintenance = null
  categoryComponentIdentification = null
  priceList = null
  title = ''
  formSocialNetworks: Record<string, any> = {}
  networksKeys = []
  formData: Record<string, any> = {}
  errorMessages: Record<string, string> = {}
  fields: Record<string, any> = {}
  metadata = {}
  metadataCollection = {}
  alert = {
    open: false,
    title: '',
  }

  get change () {
    const { formData } = this

    return stringifySafe([formData])
  }

  get stockHasPrice () {
    const { stock } = this

    return Boolean(stock?.currentPrice?.amount)
  }

  get stockHasPhotos () {
    const { formData, fileParametersVideo, fileParametersPhoto } = this
    let hasFiles = true

    const keyPhoto = fileParametersPhoto.filter(file => file.required).map(file => file.description)
    const keyVid = fileParametersVideo.filter(file => file.required).map(file => file.description)

    for (const key of [...keyPhoto, ...keyVid]) {
      if (!formData?.[key]?.photo?.length) {
        hasFiles = false
      }
    }

    return hasFiles
  }

  get stockForPublish () {
    const { stockHasPrice, stockHasPhotos } = this

    return stockHasPrice && stockHasPhotos
  }

  async mounted () {
    await this.setMetadata()
    const { uid, id, title, metadataCollection } = this
    const enablementId = uid || id

    if (isValidNumber(enablementId)) {
      await this.getStockInfo(enablementId)
    }

    if (!this.isBreadCrumbPresent(title)) {
      this.setFormCrumbs(metadataCollection, title, Boolean(enablementId))
    }

    this.displayDetail()
  }

  async setMetadata () {
    const { metadata } = this.getForm('Stock', 'advertiser_publish_stock')
    const { form } = metadata as Form
    this.title = form.title
    this.metadataCollection = metadata
    const process = await this.fetchData({
      query: { name: 'find', model: 'Process' },
      filter: { table_name: { _eq: 'stock' } },
    })

    this.idProcess = process[0].id
    const photos = await this.fetchData({
      query: { name: 'find', model: 'FileParameter' },
      filter: { _and: [{ process: { table_name: { _eq: 'stock' } } }, { file_type: { name: { _eq: 'photo' } } }] },
    })

    this.fileParametersPhoto = photos.sort((a, b) => a.visibility?.form?.order - b?.visibility?.form?.order)
    this.componentKeysPhoto = this.fileParametersPhoto.map(fileParameter => fileParameter.description)

    const videos = await this.fetchData({
      query: { name: 'find', model: 'FileParameter' },
      filter: { _and: [{ process: { table_name: { _eq: 'stock' } } }, { file_type: { name: { _eq: 'video' } } }] },
    })

    this.fileParametersVideo = videos.sort((a, b) => a.visibility?.form?.order - b.visibility?.form?.order)
    this.componentKeysVideo = this.fileParametersVideo.map(fileParameter => fileParameter.description)

    this.generateFormInfo([...this.fileParametersPhoto, ...this.fileParametersVideo])
    this.generateSocialNetworksForm(process?.[0]?.config?.socialNetworks)
  }

  generateSocialNetworksForm (networks) {
    const keys = Object.keys(networks)
    for (const key of keys) {
      if (networks[key]) {
        this.networksKeys.push(key)
        this.$set(this.formSocialNetworks, key, { url: '' })
      }
    }
  }

  generateFormInfo (components) {
    for (const field of components) {
      const fieldKey = field.description

      this.$set(this.formData, fieldKey, { photo: [] })
      this.$set(this.errorMessages, fieldKey, '')
      this.$set(this.fields, fieldKey, {
        properties: {
          accept: '',
          multiple: false,
          fileTypeId: null,
          name: '',
          label: field.description,
        },
      })

      this.setProperties(field, fieldKey)
    }
    this.ready = true
  }

  setProperties (fileInfo, fieldKey) {
    const info = fileInfo
    if (info) {
      this.fields[fieldKey].properties.accept = info.fileType.mimes
      this.fields[fieldKey].properties.multiple = info.multiple
      this.fields[fieldKey].properties.fileTypeId = info.fileType.id
      this.fields[fieldKey].properties.name = info.name
      this.fields[fieldKey].properties.required = info.required
      this.fields[fieldKey].properties.label = info.description
    }
  }

  async getStockInfo (id) {
    this.stock = await this.fetchData({
      query: { name: 'fetch', model: 'StockAdvertiser', params: { id } },
      force: true,
    })

    if (this.stock.metadata?.socialNetworks) {
      this.setSocialNetworksForm(this.stock.metadata?.socialNetworks)
    }
  }

  setSocialNetworksForm (networks) {
    const keys = Object.keys(networks)
    for (const key of keys) {
      if (networks[key]?.url) {
        this.formSocialNetworks[key].url = networks[key].url
      }
    }
  }

  displayDetail () {
    const { stock, metadataCollection } = this

    this.metadata = {
      data: stock,
      metadata: metadataCollection,
    }

    this.showDetail = Boolean(stock.id)
  }

  async send () {
    if (!this.$refs.form.validate()) {
      return
    }
    this.checkForDuplicatePhotos()
    const hasErrors = Object.values(this.errorMessages).some(message => message !== '')
    if (hasErrors) {
      return
    }

    this.loadingForm = true
    await this.sendPhotos()
    await this.sendSocialNetworks()
    await this.publishStock(this.stock.id)
    this.loadingForm = false

    await this.close()
  }

  async sendSocialNetworks () {
    const { formSocialNetworks, stock } = this

    const socialNetworks = Object.keys(formSocialNetworks).reduce((acc, key) => {
      acc[key] = { url: formSocialNetworks[key].url }
      return acc
    }, {})

    if (stock.metadata?.socialNetworks) {
      stock.metadata.socialNetworks = socialNetworks as SocialNetworks
    } else {
      stock.metadata = { socialNetworks }
    }
    await this.pushData({
      model: 'Stock',
      fields: {
        id: stock.id,
        metadata: stock.metadata,
      },
    })
  }

  async sendPhotos () {
    const { formData, fields, stock, componentKeysPhoto, componentKeysVideo, idProcess } = this

    await Promise.all(
      componentKeysPhoto.map(async field => {
        await this.handleFileType(formData[field].photo, fields[field], idProcess, stock.id, null)
      })
    )

    await Promise.all(
      componentKeysVideo.map(async field => {
        await this.handleFileType(formData[field].photo, fields[field], idProcess, stock.id, null)
      })
    )
  }

  loadingFile (flag) {
    this.isUploadingFile = flag
  }

  checkForDuplicatePhotos () {
    const seen = new Set()
    for (const key in this.formData) {
      const photos = this.formData[key].photo

      for (const photo of photos) {
        if (seen.has(photo.id)) {
          this.alert.open = true
          this.alert.title = `El campo ${key} tiene un archivo repetido.`
          this.$set(this.errorMessages, key, 'Archivo repetido detectado.')
        } else {
          seen.add(photo.id)
          this.$set(this.errorMessages, key, '')
        }
      }
    }
  }

  cleanFields () {
    this.alert.open = false
    this.alert.title = ''

    const key = Object.keys(this.errorMessages)
    const keysToClean = key.filter(key => this.errorMessages[key] !== '')

    keysToClean.forEach(key => {
      this.$set(this.errorMessages, key, '')
      this.formData[key].photo = []
    })
  }

  @Watch('fileParametersPhoto', { immediate: true, deep: true })
  @Watch('fileParametersVideo', { immediate: true, deep: true })
  async onFileParametersChange (fileParameters) {
    const newPhotos = []
    const { uid } = this

    const handleComponentChange = async parameter => {
      const photos = await this.fetchData({
        query: { name: 'find', model: 'FileProcess' },
        filter: {
          _and: [
            { id_process_record: { _eq: uid } },
            { id_file_parameter: { _eq: parameter.id } },
          ],
        },
        force: true,
      })

      if (photos.length) {
        newPhotos.push(...photos)
      }
    }

    for (const parameter of fileParameters) {
      await handleComponentChange(parameter)
    }

    for (const key of this.componentKeysPhoto) {
      const file = newPhotos.filter(photo => photo.parameter.description === key)

      if (file?.length) {
        this.formData[key].photo = file
      }
    }

    for (const key of this.componentKeysVideo) {
      const file = newPhotos.filter(photo => photo.parameter.description === key)

      if (file?.length) {
        this.formData[key].photo = file
      }
    }
  }

  @Watch('formData', { immediate: true, deep: true })
  onFormDataChange () {
    this.checkForDuplicatePhotos()
  }
  }
